import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Input,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Typography,
  Divider,
  Alert,
  Statistic,
  Badge,
  Tooltip,
  Radio,
  InputNumber,
  Pagination,
  Menu,
  Checkbox,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  PrinterOutlined,
  BarcodeOutlined,
  TruckOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SettingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import type { 
  Waybill, 
  BatchPickingList, 
  OutboundOrder
} from '../../types/picking';
import apiService from '../../../../services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

interface WaybillManagementProps {
  batchPickingLists?: BatchPickingList[];
  onClose?: () => void;
}

const WaybillManagement: React.FC<WaybillManagementProps> = ({ 
  batchPickingLists = [], 
  onClose 
}) => {
  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null] | null>(null);
  const [createWaybillVisible, setCreateWaybillVisible] = useState(false);
  const [waybillDetailVisible, setWaybillDetailVisible] = useState(false);
  const [selectedWaybill, setSelectedWaybill] = useState<Waybill | null>(null);
  const [selectedBatchPicking, setSelectedBatchPicking] = useState<BatchPickingList | null>(null);
  // 可選的批量揀貨單（若父層未傳入，這裡自動載入）
  const [availableBatches, setAvailableBatches] = useState<BatchPickingList[]>(batchPickingLists || []);
  // 編輯配貨單
  const [editVisible, setEditVisible] = useState(false);
  const [editingWaybill, setEditingWaybill] = useState<Waybill | null>(null);
  const [editForm] = Form.useForm();
  
  // 配貨單表格排序狀態
  const [waybillSortField, setWaybillSortField] = useState<string>('');
  const [waybillSortOrder, setWaybillSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 配貨單表格列配置狀態
  const [waybillColumnSettings, setWaybillColumnSettings] = useState(() => {
    const saved = localStorage.getItem('waybill-columns-config');
    const version = localStorage.getItem('waybill-columns-version');
    
    if (version !== '2025-02-v1' || !saved) {
      const defaultConfig = {
        waybillNumber: { visible: true, order: 0 },
        pickOrderNumber: { visible: true, order: 1 },
        trackingNumber: { visible: true, order: 2 },
        deliveryMethod: { visible: true, order: 3 },
        deliveryCompany: { visible: true, order: 4 },
        receiverName: { visible: true, order: 5 },
        receiverCompany: { visible: true, order: 6 },
        receiverPhone: { visible: true, order: 7 },
        packageWeight: { visible: true, order: 8 },
        packageItemCount: { visible: true, order: 9 },
        packageValue: { visible: true, order: 10 },
        status: { visible: true, order: 11 },
        scheduledPickupTime: { visible: true, order: 12 },
        scheduledDeliveryTime: { visible: true, order: 13 },
        createdAt: { visible: false, order: 14 },
        actions: { visible: true, order: 15 }
      };
      localStorage.setItem('waybill-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('waybill-columns-version', '2025-02-v1');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        waybillNumber: { visible: true, order: 0 },
        pickOrderNumber: { visible: true, order: 1 },
        trackingNumber: { visible: true, order: 2 },
        deliveryMethod: { visible: true, order: 3 },
        deliveryCompany: { visible: true, order: 4 },
        receiverName: { visible: true, order: 5 },
        receiverCompany: { visible: true, order: 6 },
        receiverPhone: { visible: true, order: 7 },
        packageWeight: { visible: true, order: 8 },
        packageItemCount: { visible: true, order: 9 },
        packageValue: { visible: true, order: 10 },
        status: { visible: true, order: 11 },
        scheduledPickupTime: { visible: true, order: 12 },
        scheduledDeliveryTime: { visible: true, order: 13 },
        createdAt: { visible: false, order: 14 },
        actions: { visible: true, order: 15 }
      };
      return defaultConfig;
    }
  });
  
  const [form] = Form.useForm();

  // 導出（CSV）
  const handleExportExcel = () => {
    try {
      const headers = [
        '配貨單編號','揀貨單編號','追蹤號碼','配送方式','配送公司','收件人','收件公司','收件電話','重量(kg)','件數','價值','狀態','預計取件','預計送達','創建時間'
      ];
      const rows = sortedWaybills.map(w => [
        w.waybillNumber || '',
        w.pickOrderNumber || '',
        w.trackingNumber || '',
        w.deliveryMethod === '3rd_party' ? '第三方' : '自配送',
        w.deliveryCompany || '',
        w.receiverInfo?.name || '',
        w.receiverInfo?.company || '',
        w.receiverInfo?.phone || '',
        String(w.packageInfo?.weight ?? ''),
        String(w.packageInfo?.itemCount ?? ''),
        String(w.packageInfo?.totalValue ?? ''),
        getStatusText(w.status),
        w.scheduledPickupTime ? moment(w.scheduledPickupTime).format('YYYY-MM-DD HH:mm') : '',
        w.scheduledDeliveryTime ? moment(w.scheduledDeliveryTime).format('YYYY-MM-DD HH:mm') : '',
        w.createdAt ? moment(w.createdAt).format('YYYY-MM-DD HH:mm') : ''
      ]);
      const csvContent = [headers, ...rows].map(r => r.map(v => `${String(v).replace(/"/g,'""')}`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `waybills_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error('導出失敗，請稍後重試');
    }
  };

  // 處理配貨單列排序
  const handleWaybillColumnSort = (columnKey: string) => {
    if (waybillSortField === columnKey) {
      setWaybillSortOrder(waybillSortOrder === 'asc' ? 'desc' : waybillSortOrder === 'desc' ? '' : 'asc');
      if (waybillSortOrder === 'desc') {
        setWaybillSortField('');
      }
    } else {
      setWaybillSortField(columnKey);
      setWaybillSortOrder('asc');
    }
  };

  // 獲取配貨單排序圖標
  const getWaybillSortIcon = (columnKey: string) => {
    if (waybillSortField !== columnKey) return null;
    return waybillSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理配貨單列可見性切換
  const handleWaybillColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...waybillColumnSettings,
      [columnKey]: { ...waybillColumnSettings[columnKey], visible }
    };
    setWaybillColumnSettings(newSettings);
    localStorage.setItem('waybill-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('waybill-columns-version', '2025-02-v1');
  };

  // Mock delivery companies
  const deliveryCompanies = [
    { code: 'SF', name: '順豐速運', trackingPrefix: 'SF' },
    { code: 'YTO', name: '圓通速遞', trackingPrefix: 'YT' },
    { code: 'ZTO', name: '中通快遞', trackingPrefix: 'ZTO' },
    { code: 'STO', name: '申通快遞', trackingPrefix: 'STO' },
    { code: 'YD', name: '韻達速遞', trackingPrefix: 'YD' },
    { code: 'EMS', name: '中國郵政EMS', trackingPrefix: 'EMS' },
    { code: 'JD', name: '京東物流', trackingPrefix: 'JD' },
    { code: 'SELF', name: '自配送', trackingPrefix: 'SELF' }
  ];

  // 已移除 mock 列表數據，統一從後端載入

  useEffect(() => {
    loadWaybills();
  }, [page, pageSize, search, statusFilter, waybillSortField, waybillSortOrder, dateRange]);

  // 當父層有傳入批量揀貨單時，同步到本地；否則由本地自行載入
  useEffect(() => {
    if (Array.isArray(batchPickingLists) && batchPickingLists.length > 0) {
      setAvailableBatches(batchPickingLists);
    }
  }, [batchPickingLists]);

  const loadPickingOptions = async () => {
    try {
      const resp: any = await apiService.getPickingLists({ page: 1, limit: 100, sortField: 'created_at', sortOrder: 'desc' });
      const rows: any[] = resp?.data || resp || [];
      const mapped: BatchPickingList[] = rows.map((r: any) => ({
        id: String(r.id),
        batchPickingNumber: r.batchPickingNumber || r.batch_picking_no,
        batchPickingBarcode: r.batchPickingBarcode || r.batch_picking_barcode || '',
        status: r.status || 'processing',
        scheduledDate: r.scheduledDate ? new Date(r.scheduledDate) : new Date(),
        createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
        updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
        outboundOrders: [],
        pickOrderNumbers: r.pickOrderNumbers || [],
        totalOrders: Number(r.totalOrders || 0),
        totalItems: Number(r.totalItems || 0),
        totalQuantity: Number(r.totalQuantity || 0),
        locationFrom: r.locationFrom || '',
        locationTo: r.locationTo || '',
        consolidatedItems: [],
        assignedTo: r.assignedTo || r.picker || undefined,
        discrepancies: []
      }));
      setAvailableBatches(mapped);
    } catch (e) {
      // 靜默
    }
  };

  // 從後端響應中提取總數，兼容多種鍵名
  const getTotalFromResponse = (resp: any, fallbackCount: number) => {
    const candidates = [
      resp?.pagination?.total,
      resp?.total,
      resp?.totalCount,
      resp?.count,
      resp?.meta?.total,
      resp?.meta?.pagination?.total
    ];
    const firstValid = candidates.find(v => typeof v === 'number' && v >= 0);
    return (firstValid as number | undefined) ?? fallbackCount;
  };

  const loadWaybills = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (waybillSortField && waybillSortOrder) {
        params.sortField = waybillSortField;
        params.sortOrder = waybillSortOrder as 'asc' | 'desc';
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.dateFrom = (dateRange[0] as any).startOf('day').toISOString();
        params.dateTo = (dateRange[1] as any).endOf('day').toISOString();
      }
      const resp = await apiService.getWaybills(params);
      const mapped: Waybill[] = (resp.data || []).map((w: any) => ({
        id: w.id,
        waybillNumber: w.waybillNumber,
        batchPickingId: w.batchPickingId || '',
        outboundOrderId: w.outboundOrderId || '',
        pickOrderNumber: w.pickOrderNumber || '',
        senderInfo: {
          name: w.senderName || '',
          address: w.senderAddress || '',
          phone: w.senderPhone || '',
          company: w.senderCompany || ''
        },
        receiverInfo: {
          name: w.receiverName || '',
          address: w.receiverAddress || '',
          phone: w.receiverPhone || '',
          company: w.receiverCompany || ''
        },
        deliveryMethod: w.deliveryMethod || '3rd_party',
        deliveryCompany: w.deliveryCompany || undefined,
        trackingNumber: w.trackingNumber || undefined,
        packageInfo: {
          weight: Number(w.packageWeight ?? 0),
          dimensions: w.packageDimensions || '',
          itemCount: Number(w.packageItemCount ?? 0),
          totalValue: Number(w.packageValue ?? 0)
        },
        scheduledPickupTime: w.scheduledPickupTime ? new Date(w.scheduledPickupTime) : undefined,
        scheduledDeliveryTime: w.scheduledDeliveryTime ? new Date(w.scheduledDeliveryTime) : undefined,
        status: w.status,
        notes: w.notes,
        specialInstructions: w.specialInstructions,
        createdAt: new Date(w.createdAt),
        updatedAt: new Date(w.updatedAt)
      }));
      setWaybills(mapped);
      // 兼容不同後端返回格式，確保分頁控件獲得正確總數
      setTotal(getTotalFromResponse(resp, mapped.length));
    } catch (e: any) {
      message.error(e?.message || '加載配貨單信息失敗');
    } finally {
      setLoading(false);
    }
  };

  const generateTrackingNumber = (deliveryCompany: string): string => {
    const company = deliveryCompanies.find(c => c.name === deliveryCompany);
    const prefix = company?.trackingPrefix || 'TRK';
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${timestamp}${random}`;
  };

  const handleCreateWaybill = async (values: any) => {
    setLoading(true);
    try {
      if (!selectedBatchPicking) {
        message.error('請選擇批量揀貨單');
        return;
      }

      // 按批量揀貨單生成單一配貨單（與後端結構對齊）
      const waybillNumber = `WB-${moment().format('YYYYMMDD')}-${Math.random().toString().slice(2,5)}`;
      const trackingNumber = values.deliveryMethod === '3rd_party'
        ? generateTrackingNumber(values.deliveryCompany)
        : `SELF-${waybillNumber}`;
      const payload = {
        id: `WB_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
        waybillNumber,
        trackingNumber,
        status: 'created',
        deliveryMethod: values.deliveryMethod,
        deliveryCompany: values.deliveryCompany || null,
        receiverName: values.receiverName || '',
        receiverCompany: values.receiverCompany || '',
        receiverPhone: values.receiverPhone || '',
        receiverAddress: values.receiverAddress || '',
        senderName: values.senderName,
        senderCompany: values.senderCompany,
        senderPhone: values.senderPhone,
        senderAddress: values.senderAddress,
        packageWeight: values.weight ?? 1.0,
        packageItemCount: Number(selectedBatchPicking.totalItems ?? 0),
        packageValue: values.totalValue ?? 0,
        packageDimensions: values.dimensions || '30x20x15cm',
        pickOrderNumber: selectedBatchPicking.batchPickingNumber,
        scheduledDeliveryTime: values.scheduledDeliveryTime ? moment(values.scheduledDeliveryTime).toISOString() : null,
        scheduledPickupTime: values.scheduledPickupTime ? moment(values.scheduledPickupTime).toISOString() : null,
        notes: values.notes || null,
        specialInstructions: values.specialInstructions || null,
      };

      await apiService.createWaybill(payload);
      message.success('保存成功');
      setCreateWaybillVisible(false);
      form.resetFields();
      setSelectedBatchPicking(null);
      await loadWaybills();
    } catch (e: any) {
      message.error(e?.message || '保存失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintWaybill = async (waybill: Waybill) => {
    // 只預覽PDF：下載後開新分頁顯示
    try {
      const { blob } = await apiService.downloadWaybillPDF({ waybillNumber: waybill.waybillNumber, orderNumber: waybill.pickOrderNumber });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        message.error('無法開啟PDF預覽視窗，請允許彈窗');
        URL.revokeObjectURL(url);
        return;
      }
      try { win.focus(); } catch {}
      const revoke = () => { try { URL.revokeObjectURL(url); } catch {} };
      (win as any).onbeforeunload = revoke;
      (win as any).onunload = revoke;
    } catch (e) {
      console.error('下載配貨單PDF失敗', e);
      message.error('無法載入配貨單 PDF，請稍後重試');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'created': 'blue',
      'printed': 'orange',
      'picked_up': 'purple',
      'in_transit': 'cyan',
      'delivered': 'green',
      'failed': 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'created': '已創建',
      'printed': '已列印',
      'picked_up': '已取件',
      'in_transit': '運輸中',
      'delivered': '已送達',
      'failed': '配送失敗'
    };
    return texts[status] || status;
  };

  // 定義所有可用的配貨單列
  const allWaybillColumns = {
    waybillNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('waybillNumber')}
        >
          配貨單編號
          {getWaybillSortIcon('waybillNumber')}
        </div>
      ),
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
      width: 120,
      sorter: true,
      render: (text) => text
    },
    pickOrderNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('pickOrderNumber')}
        >
          揀貨單編號
          {getWaybillSortIcon('pickOrderNumber')}
        </div>
      ),
      dataIndex: 'pickOrderNumber',
      key: 'pickOrderNumber',
      width: 120,
      sorter: true,
      render: (text) => text
    },
    trackingNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('trackingNumber')}
        >
          追蹤號碼
          {getWaybillSortIcon('trackingNumber')}
        </div>
      ),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      width: 140,
      sorter: true,
      render: (text) => (
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {text}
        </div>
      )
    },
    deliveryMethod: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('deliveryMethod')}
        >
          配送類型
          {getWaybillSortIcon('deliveryMethod')}
        </div>
      ),
      dataIndex: 'deliveryMethod',
      key: 'deliveryMethod',
      width: 100,
      sorter: true,
      render: (text) => (
        <div style={{ fontWeight: 'bold' }}>
          {text === '3rd_party' ? '第三方' : '自配送'}
        </div>
      )
    },
    deliveryCompany: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('deliveryCompany')}
        >
          快遞公司
          {getWaybillSortIcon('deliveryCompany')}
        </div>
      ),
      dataIndex: 'deliveryCompany',
      key: 'deliveryCompany',
      width: 120,
      sorter: true,
      render: (text) => text
    },
    receiverName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('receiverInfo.name')}
        >
          收件人姓名
          {getWaybillSortIcon('receiverInfo.name')}
        </div>
      ),
      key: 'receiverName',
      width: 120,
      sorter: true,
      render: (_, record) => (
        <div style={{ fontWeight: 'bold' }}>
          {record.receiverInfo.name}
        </div>
      )
    },
    receiverCompany: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('receiverInfo.company')}
        >
          收件人公司
          {getWaybillSortIcon('receiverInfo.company')}
        </div>
      ),
      key: 'receiverCompany',
      width: 140,
      sorter: true,
      render: (_, record) => record.receiverInfo.company
    },
    receiverPhone: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('receiverInfo.phone')}
        >
          收件人電話
          {getWaybillSortIcon('receiverInfo.phone')}
        </div>
      ),
      key: 'receiverPhone',
      width: 130,
      sorter: true,
      render: (_, record) => record.receiverInfo.phone
    },
    packageWeight: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('packageInfo.weight')}
        >
          包裹重量
          {getWaybillSortIcon('packageInfo.weight')}
        </div>
      ),
      key: 'packageWeight',
      width: 100,
      sorter: true,
      render: (_, record) => `${record.packageInfo.weight}kg`
    },
    packageItemCount: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('packageInfo.itemCount')}
        >
          包裹件數
          {getWaybillSortIcon('packageInfo.itemCount')}
        </div>
      ),
      key: 'packageItemCount',
      width: 100,
      sorter: true,
      render: (_, record) => `${record.packageInfo.itemCount}件`
    },
    packageValue: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('packageInfo.totalValue')}
        >
          包裹價值
          {getWaybillSortIcon('packageInfo.totalValue')}
        </div>
      ),
      key: 'packageValue',
      width: 120,
      sorter: true,
      render: (_, record) => `$${record.packageInfo.totalValue.toFixed(2)}`
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('status')}
        >
          狀態
          {getWaybillSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    scheduledPickupTime: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('scheduledPickupTime')}
        >
          預計取件時間
          {getWaybillSortIcon('scheduledPickupTime')}
        </div>
      ),
      key: 'scheduledPickupTime',
      width: 130,
      sorter: true,
      render: (_, record) => record.scheduledPickupTime ? 
        moment(record.scheduledPickupTime).format('YYYY-MM-DD HH:mm') : '-'
    },
    scheduledDeliveryTime: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('scheduledDeliveryTime')}
        >
          預計送達時間
          {getWaybillSortIcon('scheduledDeliveryTime')}
        </div>
      ),
      key: 'scheduledDeliveryTime',
      width: 130,
      sorter: true,
      render: (_, record) => record.scheduledDeliveryTime ? 
        moment(record.scheduledDeliveryTime).format('YYYY-MM-DD HH:mm') : '-'
    },
    createdAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWaybillColumnSort('createdAt')}
        >
          創建時間
          {getWaybillSortIcon('createdAt')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      sorter: true,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm')
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedWaybill(record);
              setWaybillDetailVisible(true);
            }}
          >
            詳情
          </Button>
          <Button 
            size="small"
            onClick={() => {
              setEditingWaybill(record);
              editForm.setFieldsValue({
                trackingNumber: record.trackingNumber,
                deliveryMethod: record.deliveryMethod || '3rd_party',
                deliveryCompany: record.deliveryCompany,
                receiverName: record.receiverInfo?.name,
                receiverCompany: record.receiverInfo?.company,
                receiverPhone: record.receiverInfo?.phone,
                receiverAddress: record.receiverInfo?.address,
                packageWeight: record.packageInfo?.weight,
                packageItemCount: record.packageInfo?.itemCount,
                packageValue: record.packageInfo?.totalValue,
                packageDimensions: record.packageInfo?.dimensions,
                scheduledPickupTime: record.scheduledPickupTime ? moment(record.scheduledPickupTime) : undefined,
                scheduledDeliveryTime: record.scheduledDeliveryTime ? moment(record.scheduledDeliveryTime) : undefined,
                notes: record.notes,
                specialInstructions: record.specialInstructions,
              });
              setEditVisible(true);
            }}
          >
            編輯
          </Button>
          <Button 
            size="small" 
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => handlePrintWaybill(record)}
          >
            列印
          </Button>
          <Button 
            size="small" 
            danger
            onClick={async () => {
              Modal.confirm({
                title: '確認刪除？',
                content: `將刪除配貨單 ${record.waybillNumber}`,
                onOk: async () => {
                  try {
                    // 後端刪除
                    await apiService.deleteWaybill(record.id);
                    message.success('刪除成功');
                    // 立即刷新列表，避免當前頁碼未變更時 useEffect 不觸發
                    await loadWaybills();
                  } catch (e: any) {
                    message.error(e?.message || '刪除失敗');
                  }
                }
              });
            }}
          >
            刪除
          </Button>
        </Space>
      )
    }
  };

  // 配貨單列設置菜單
  const waybillColumnSettingsMenu = (
    <Menu>
      <Menu.Item key="header" disabled>
        <Text strong>列設置</Text>
      </Menu.Item>
      <Menu.Divider />
      {Object.entries(allWaybillColumns).map(([key, column]) => (
        <Menu.Item key={key}>
          <Checkbox
            checked={waybillColumnSettings[key]?.visible ?? true}
            onChange={(e) => handleWaybillColumnVisibilityChange(key, e.target.checked)}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'waybillNumber' ? '配貨單編號' :
             key === 'pickOrderNumber' ? '揀貨單編號' :
             key === 'trackingNumber' ? '追蹤號碼' :
             key === 'deliveryMethod' ? '配送類型' :
             key === 'deliveryCompany' ? '快遞公司' :
             key === 'receiverName' ? '收件人姓名' :
             key === 'receiverCompany' ? '收件人公司' :
             key === 'receiverPhone' ? '收件人電話' :
             key === 'packageWeight' ? '包裹重量' :
             key === 'packageItemCount' ? '包裹件數' :
             key === 'packageValue' ? '包裹價值' :
             key === 'status' ? '狀態' :
             key === 'scheduledPickupTime' ? '預計取件時間' :
             key === 'scheduledDeliveryTime' ? '預計送達時間' :
             key === 'createdAt' ? '創建時間' :
             key === 'actions' ? '操作' : key}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  // 根據列配置生成顯示的配貨單列
  const waybillColumns: ColumnsType<Waybill> = Object.entries(waybillColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allWaybillColumns[key as keyof typeof allWaybillColumns];
      if (!column) {
        console.warn(`Missing waybill column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 處理配貨單數據排序
  const sortedWaybills = useMemo(() => {
    let result = [...waybills];
    
    if (waybillSortField && waybillSortOrder) {
      result.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        // 處理嵌套字段
        if (waybillSortField.includes('.')) {
          const fields = waybillSortField.split('.');
          aValue = fields.reduce((obj, field) => obj?.[field], a);
          bValue = fields.reduce((obj, field) => obj?.[field], b);
        } else {
          aValue = a[waybillSortField as keyof Waybill];
          bValue = b[waybillSortField as keyof Waybill];
        }
        
        // 處理數字字段排序
        if (['packageInfo.weight', 'packageInfo.itemCount', 'packageInfo.totalValue'].includes(waybillSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['createdAt', 'scheduledPickupTime', 'scheduledDeliveryTime'].includes(waybillSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return waybillSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return waybillSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [waybills, waybillSortField, waybillSortOrder]);

  return (
    <div style={{ padding: '24px' }}>

      {/* Filters - Waybill */}
      <Card style={{ marginBottom: '16px' }}>
        <Space size={16} wrap>
          <Input
            allowClear
            style={{ width: 260 }}
            placeholder="搜尋 配貨單/追蹤/收件人/公司"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <Select
            allowClear
            placeholder="狀態"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          >
            <Option value="created">已創建</Option>
            <Option value="printed">已列印</Option>
            <Option value="picked_up">已取件</Option>
            <Option value="in_transit">運輸中</Option>
            <Option value="delivered">已送達</Option>
            <Option value="failed">配送失敗</Option>
          </Select>
          <RangePicker
            value={dateRange as any}
            onChange={(v) => { setDateRange(v as any); setPage(1); }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); loadWaybills(); }} loading={loading}>刷新</Button>
          <Button icon={<FileTextOutlined />} onClick={handleExportExcel}>導出</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateWaybillVisible(true)}>生成配貨單</Button>
        </Space>
      </Card>

      {/* Main Content */}
      <Card
        title={
          <Space>
            <TruckOutlined />
            <span>配貨單列表</span>
            <Badge count={waybills.length} />
          </Space>
        }
        extra={
          <Space>
            <Dropdown overlay={waybillColumnSettingsMenu} trigger={['click']}>
              <Button icon={<SettingOutlined />}>
                列設置
              </Button>
            </Dropdown>
          </Space>
        }
      >
        <Table
          columns={waybillColumns}
          dataSource={sortedWaybills}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (totalCount, range) => `顯示 ${range[0]}-${range[1]} 項，共 ${totalCount} 項記錄` ,
            onChange: (p, ps) => {
              const newSize = ps || pageSize;
              const newPage = p || 1;
              if (newSize !== pageSize) {
                setPageSize(newSize);
                setPage(1);
              } else {
                setPage(newPage);
              }
            }
          }}
          scroll={{ x: 2600 }}
          onChange={(pagination) => {
            const newSize = pagination.pageSize || pageSize;
            const newPage = pagination.current || 1;
            if (newSize !== pageSize) {
              setPageSize(newSize);
              setPage(1);
            } else {
              setPage(newPage);
            }
          }}
        />
      </Card>

      {/* Create Waybill Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <span>生成配貨單</span>
          </Space>
        }
        open={createWaybillVisible}
        onCancel={() => setCreateWaybillVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateWaybill}
        >
          {/* Select Batch Picking */}
          <Alert
            message="請選擇要生成配貨單的批量揀貨單"
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
          
          <Form.Item
            label="批量揀貨單"
            name="batchPickingId"
            rules={[{ required: true, message: '請選擇批量揀貨單' }]}
          >
            <Select
              placeholder="選擇批量揀貨單"
              onChange={(value) => {
                const selected = availableBatches.find(bp => bp.id === value);
                setSelectedBatchPicking(selected || null);
                try { form.setFieldsValue({ batchPickingId: value }); } catch {}
              }}
              onDropdownVisibleChange={(open) => {
                if (open && availableBatches.length === 0) {
                  loadPickingOptions();
                }
              }}
              notFoundContent="暫無可用的批量揀貨單"
            >
              {availableBatches.map(picking => (
                <Option key={picking.id} value={picking.id}>
                  {picking.batchPickingNumber} - {picking.totalOrders}個訂單
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedBatchPicking && (
            <Alert
              message={`將為 ${selectedBatchPicking.totalOrders} 個訂單生成配貨單`}
              description={`揀貨單編號: ${selectedBatchPicking.batchPickingNumber}, 總數量: ${selectedBatchPicking.totalQuantity}`}
              type="success"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Delivery Method */}
          <Form.Item
            label="配送方式"
            name="deliveryMethod"
            initialValue="3rd_party"
            rules={[{ required: true, message: '請選擇配送方式' }]}
          >
            <Radio.Group>
              <Radio value="3rd_party">第三方配送</Radio>
              <Radio value="self_delivery">自配送</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.deliveryMethod !== currentValues.deliveryMethod
            }
          >
            {({ getFieldValue }) => {
              const deliveryMethod = getFieldValue('deliveryMethod');
              return deliveryMethod === '3rd_party' ? (
                <Form.Item
                  label="配送公司"
                  name="deliveryCompany"
                  rules={[{ required: true, message: '請選擇配送公司' }]}
                >
                  <Select placeholder="選擇配送公司">
                    {deliveryCompanies.filter(c => c.code !== 'SELF').map(company => (
                      <Option key={company.code} value={company.name}>
                        {company.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          {/* Sender Information */}
          <Divider orientation="left">寄件人信息</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="寄件人姓名"
                name="senderName"
                initialValue="Your Company Name"
                rules={[{ required: true, message: '請輸入寄件人姓名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="寄件公司"
                name="senderCompany"
                initialValue="Your Company Name"
                rules={[{ required: true, message: '請輸入寄件公司' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="寄件人電話"
                name="senderPhone"
                initialValue="+886-2-1234-5678"
                rules={[{ required: true, message: '請輸入寄件人電話' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="寄件地址"
                name="senderAddress"
                initialValue="123 Business Street, Taipei, Taiwan 10048"
                rules={[{ required: true, message: '請輸入寄件地址' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Receiver Information (Optional - will use order info if not provided) */}
          <Divider orientation="left">收件人信息 (可選，留空則使用訂單信息)</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="收件人姓名" name="receiverName">
                <Input placeholder="留空使用訂單客戶名稱" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="收件人電話" name="receiverPhone">
                <Input placeholder="輸入收件人電話" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="收件地址" name="receiverAddress">
            <Input placeholder="留空使用訂單配送地址" />
          </Form.Item>

          {/* Package Information */}
          <Divider orientation="left">包裹信息</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="重量 (kg)"
                name="weight"
                initialValue={1.0}
              >
                <InputNumber min={0.1} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="尺寸"
                name="dimensions"
                initialValue="30x20x15cm"
              >
                <Input placeholder="長x寬x高cm" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="預計價值" name="totalValue">
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  style={{ width: '100%' }}
                  placeholder="自動計算"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Scheduling */}
          <Divider orientation="left">配送安排</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="預計取件時間" name="scheduledPickupTime">
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="預計送達時間" name="scheduledDeliveryTime">
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Notes */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="備註" name="notes">
                <TextArea rows={3} placeholder="輸入備註信息..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="特殊說明" name="specialInstructions">
                <TextArea rows={3} placeholder="輸入特殊處理說明..." />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateWaybillVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Waybill Detail Modal */}
      <Modal
        title={
          selectedWaybill && (
            <Space>
              <FileTextOutlined />
              <span>配貨單詳情 - {selectedWaybill.waybillNumber}</span>
            </Space>
          )
        }
        open={waybillDetailVisible}
        onCancel={() => setWaybillDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setWaybillDetailVisible(false)}>
            關閉
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => selectedWaybill && handlePrintWaybill(selectedWaybill)}
          >
            列印配貨單
          </Button>
        ]}
        width={800}
      >
        {selectedWaybill && (
          <div>
            {/* Basic Info */}
            <Descriptions
              title="基本信息"
              bordered
              column={2}
              style={{ marginBottom: '24px' }}
            >
              <Descriptions.Item label="配貨單編號">
                {selectedWaybill.waybillNumber}
              </Descriptions.Item>
              <Descriptions.Item label="追蹤號碼">
                {selectedWaybill.trackingNumber}
              </Descriptions.Item>
              <Descriptions.Item label="揀貨單編號">
                {selectedWaybill.pickOrderNumber}
              </Descriptions.Item>
              <Descriptions.Item label="狀態">
                <Tag color={getStatusColor(selectedWaybill.status)}>
                  {getStatusText(selectedWaybill.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="配送方式">
                {selectedWaybill.deliveryMethod === '3rd_party' ? '第三方配送' : '自配送'}
              </Descriptions.Item>
              <Descriptions.Item label="配送公司">
                {selectedWaybill.deliveryCompany || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* Sender & Receiver Info */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={12}>
                <Card title="寄件人信息" size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <UserOutlined /> <strong>{selectedWaybill.senderInfo.name}</strong>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>公司:</strong> {selectedWaybill.senderInfo.company}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <PhoneOutlined /> {selectedWaybill.senderInfo.phone}
                  </div>
                  <div>
                    <EnvironmentOutlined /> {selectedWaybill.senderInfo.address}
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="收件人信息" size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <UserOutlined /> <strong>{selectedWaybill.receiverInfo.name}</strong>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>公司:</strong> {selectedWaybill.receiverInfo.company}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <PhoneOutlined /> {selectedWaybill.receiverInfo.phone}
                  </div>
                  <div>
                    <EnvironmentOutlined /> {selectedWaybill.receiverInfo.address}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Package & Delivery Info */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={12}>
                <Card title="包裹信息" size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <InboxOutlined /> <strong>重量:</strong> {selectedWaybill.packageInfo.weight}kg
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>尺寸:</strong> {selectedWaybill.packageInfo.dimensions}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>件數:</strong> {selectedWaybill.packageInfo.itemCount}件
                  </div>
                  <div>
                    <strong>價值:</strong> ${selectedWaybill.packageInfo.totalValue.toFixed(2)}
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="配送安排" size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <ClockCircleOutlined /> <strong>預計取件:</strong>
                  </div>
                  <div style={{ marginBottom: '8px', marginLeft: '20px' }}>
                    {selectedWaybill.scheduledPickupTime 
                      ? moment(selectedWaybill.scheduledPickupTime).format('YYYY-MM-DD HH:mm')
                      : '-'
                    }
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <ClockCircleOutlined /> <strong>預計送達:</strong>
                  </div>
                  <div style={{ marginLeft: '20px' }}>
                    {selectedWaybill.scheduledDeliveryTime 
                      ? moment(selectedWaybill.scheduledDeliveryTime).format('YYYY-MM-DD HH:mm')
                      : '-'
                    }
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Notes & Instructions */}
            {(selectedWaybill.notes || selectedWaybill.specialInstructions) && (
              <div>
                {selectedWaybill.notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={5}>備註</Title>
                    <div style={{ 
                      background: '#f8f9fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      border: '1px solid #e8e8e8'
                    }}>
                      {selectedWaybill.notes}
                    </div>
                  </div>
                )}
                {selectedWaybill.specialInstructions && (
                  <div>
                    <Title level={5}>特殊說明</Title>
                    <div style={{ 
                      background: '#fff7e6', 
                      padding: '12px', 
                      borderRadius: '6px',
                      border: '1px solid #ffd591'
                    }}>
                      {selectedWaybill.specialInstructions}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Waybill Modal */}
      <Modal
        title="編輯配貨單"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={async () => {
          try {
            const v = await editForm.validateFields();
            if (!editingWaybill) return;
            await apiService.updateWaybill(editingWaybill.id, {
              trackingNumber: v.trackingNumber,
              deliveryMethod: v.deliveryMethod,
              deliveryCompany: v.deliveryCompany,
              receiverName: v.receiverName,
              receiverCompany: v.receiverCompany,
              receiverPhone: v.receiverPhone,
              receiverAddress: v.receiverAddress,
              packageWeight: v.packageWeight,
              packageItemCount: v.packageItemCount,
              packageValue: v.packageValue,
              packageDimensions: v.packageDimensions,
              scheduledPickupTime: v.scheduledPickupTime?.toISOString?.(),
              scheduledDeliveryTime: v.scheduledDeliveryTime?.toISOString?.(),
              notes: v.notes,
              specialInstructions: v.specialInstructions,
            });
            message.success('保存成功');
            setEditVisible(false);
            await loadWaybills();
          } catch (e: any) {
            if (e?.errorFields) return; // 表單校驗錯誤
            message.error(e?.message || '保存失敗');
          }
        }}
        okText="確定"
        cancelText="取消"
        width={720}
      >
        <Form layout="vertical" form={editForm}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="追蹤號碼" name="trackingNumber">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="配送方式" name="deliveryMethod" rules={[{ required: true, message: '請選擇配送方式' }]}>
                <Select>
                  <Option value="3rd_party">第三方配送</Option>
                  <Option value="self_delivery">自配送</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="配送公司" name="deliveryCompany">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="包裹重量(kg)" name="packageWeight">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="包裹件數" name="packageItemCount">
                <InputNumber min={0} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="包裹價值" name="packageValue">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="包裹尺寸" name="packageDimensions">
            <Input placeholder="長x寬x高cm" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="預計取件" name="scheduledPickupTime">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="預計送達" name="scheduledDeliveryTime">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="收件人姓名" name="receiverName">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="收件人公司" name="receiverCompany">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="收件人電話" name="receiverPhone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="收件地址" name="receiverAddress">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="備註" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="特殊說明" name="specialInstructions">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WaybillManagement;
